import { FunctionComponent, useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Container,
    Stack,
    SvgIcon,
    TextField,
    Typography,
} from '@mui/material';
import DownloadIcon from '@heroicons/react/24/solid/ArrowDownTrayIcon';
import { format, startOfMonth } from 'date-fns';
import { useNotification } from '@starjack/features/notifications';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useCsvConfig } from '@/hooks/useCsvConfig';
import { UserType } from '@/models/user';
import { useLazyGetContestEntriesQuery } from '@/api/v2/contestEntries';
import {
    contestEntriesCsvConfig,
    toContestEntryCsvRows,
} from '@/pageContents/contestEntries/contestEntriesCsv';

const isoDate = (date: Date) => format(date, 'yyyy-MM-dd');

export const ContestEntriesContent: FunctionComponent = () => {
    const { userType } = useAuthentication();

    // The shared permission helpers let impersonated lift operator sessions through
    // (see ReportsPageContent). The export holds customer data of the whole platform,
    // so the request must not even be sent from such a session.
    if ((userType as unknown as UserType) === UserType.LiftOperator) {
        return (
            <Container sx={{ mt: 3, ml: 'unset' }} maxWidth={false}>
                <Alert severity='warning'>
                    Das Gewinnspiel steht nur der Plattform zur Verfügung.
                </Alert>
            </Container>
        );
    }

    return <ContestEntriesExport />;
};

const ContestEntriesExport: FunctionComponent = () => {
    const [from, setFrom] = useState(() => isoDate(startOfMonth(new Date())));
    const [to, setTo] = useState(() => isoDate(new Date()));
    const [getContestEntries, { isFetching }] = useLazyGetContestEntriesQuery();
    const { addNotification } = useNotification();
    const csvConfig = useMemo(
        () => contestEntriesCsvConfig(from, to),
        [from, to]
    );
    const { onGenerateCSVWithDownload } = useCsvConfig(csvConfig);

    const rangeIsValid = Boolean(from && to) && from <= to;

    const exportCsv = async () => {
        try {
            // Always fresh: entries keep coming in while the contest runs.
            const entries = await getContestEntries({ from, to }).unwrap();
            if (!entries.length) {
                addNotification({
                    type: 'warning',
                    message: 'Im gewählten Zeitraum gibt es keine Teilnahmen.',
                });
                return;
            }
            onGenerateCSVWithDownload(toContestEntryCsvRows(entries));
            addNotification({
                type: 'success',
                message: `${entries.length} Teilnahmen exportiert`,
            });
        } catch {
            addNotification({
                type: 'error',
                message: 'Der Export ist fehlgeschlagen.',
            });
        }
    };

    return (
        <Container sx={{ mt: 3, ml: 'unset' }} maxWidth={false}>
            <Typography sx={{ mb: 8 }} variant='h4'>
                Gewinnspiel
            </Typography>

            <Stack spacing={3}>
                <Typography variant='body2' color='text.secondary'>
                    Alle Registrierungen im Portal, bei denen ein
                    Gewinnspielcode eingegeben wurde. Die Codes werden nicht
                    geprüft.
                </Typography>

                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    alignItems={{ md: 'center' }}
                >
                    <TextField
                        label='Von'
                        type='date'
                        size='small'
                        value={from}
                        onChange={(e) => setFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label='Bis'
                        type='date'
                        size='small'
                        value={to}
                        onChange={(e) => setTo(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <Button
                        variant='outlined'
                        disabled={isFetching || !rangeIsValid}
                        onClick={exportCsv}
                        startIcon={
                            <SvgIcon fontSize='small'>
                                <DownloadIcon />
                            </SvgIcon>
                        }
                    >
                        {isFetching ? 'Wird erstellt…' : 'CSV exportieren'}
                    </Button>
                </Stack>

                {rangeIsValid ? null : (
                    <Alert severity='warning'>
                        Bitte einen vollständigen Zeitraum wählen, bei dem „Von"
                        nicht nach „Bis" liegt.
                    </Alert>
                )}
            </Stack>
        </Container>
    );
};
